import React, { useState } from 'react';
import './ContractReview.css';
import '../../components/Button/Button.css';

const ContractReview = ({ onBack }) => {
  // Parse URL parameters
  const pathParts = window.location.pathname.split('/');
  const hashrate = parseFloat(pathParts[3]) || 50;
  const contractPeriod = parseInt(pathParts[4]) || 12;
  const prepaidMonths = parseInt(pathParts[5]) || 6;
  const paymentAmount = parseFloat(pathParts[6]) || 383.16;

  const [btcPrice, setBtcPrice] = useState(120000);

  // Calculate values based on parameters
  const calculateValues = () => {
    const baseContractPricePerTH = 2.44;
    const baseElectricityPerTH = 9.45;
    const daysInPeriod = contractPeriod * 30.5; // Average days per month

    // Calculate discounts
    let volumeDiscountPercent = 0;
    if (hashrate >= 1000) volumeDiscountPercent = 5;
    else if (hashrate >= 500) volumeDiscountPercent = 4;
    else if (hashrate >= 300) volumeDiscountPercent = 3;
    else if (hashrate >= 150) volumeDiscountPercent = 2.5;
    else if (hashrate >= 50) volumeDiscountPercent = 2.78;

    let periodDiscountPercent = 0;
    if (contractPeriod >= 60) periodDiscountPercent = 10;
    else if (contractPeriod >= 36) periodDiscountPercent = 8;
    else if (contractPeriod >= 24) periodDiscountPercent = 6;
    else if (contractPeriod >= 12) periodDiscountPercent = 6;
    else if (contractPeriod >= 6) periodDiscountPercent = 3;

    // Base prices
    const baseContractPrice = baseContractPricePerTH * hashrate * contractPeriod;
    const periodDiscount = baseContractPrice * (periodDiscountPercent / 100);
    const volumeDiscount = baseContractPrice * (volumeDiscountPercent / 100);
    const contractPrice = baseContractPrice - periodDiscount - volumeDiscount;

    // Electricity
    const totalElectricityBase = baseElectricityPerTH * hashrate * contractPeriod;
    const prepaidDiscountPercent = Math.min(prepaidMonths * 2, 12);
    const prepaidElectricityAmount = totalElectricityBase * (prepaidMonths / contractPeriod);
    const prepaidDiscount = prepaidElectricityAmount * (prepaidDiscountPercent / 100);
    const prepaidElectricity = prepaidElectricityAmount - prepaidDiscount;
    const remainingElectricity = totalElectricityBase * ((contractPeriod - prepaidMonths) / contractPeriod);
    const totalElectricity = prepaidElectricity + remainingElectricity;

    // Mining rewards
    const btcPerMonthPerTH = 0.0000122;
    const totalBtcReward = btcPerMonthPerTH * hashrate * contractPeriod;
    const totalUsdReward = totalBtcReward * btcPrice;
    const totalExpenses = contractPrice + totalElectricity;
    const finalOutput = totalUsdReward - totalExpenses;
    const returnPercent = paymentAmount > 0 ? ((finalOutput / paymentAmount) * 100).toFixed(0) : 0;

    // Daily loss calculation
    const dailyBtcReward = btcPerMonthPerTH * hashrate;
    const dailyUsdReward = dailyBtcReward * btcPrice;
    const dailyExpenses = (totalExpenses / daysInPeriod);
    const dailyLoss = dailyUsdReward - dailyExpenses;

    return {
      baseContractPrice,
      periodDiscount,
      volumeDiscount,
      contractPrice,
      totalElectricityBase,
      prepaidDiscount,
      prepaidElectricity,
      remainingElectricity,
      totalElectricity,
      totalExpenses,
      totalBtcReward,
      totalUsdReward,
      finalOutput,
      returnPercent,
      periodDiscountPercent,
      prepaidDiscountPercent,
      dailyLoss,
      daysInPeriod
    };
  };

  const calculations = calculateValues();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatBTC = (value) => {
    return value.toFixed(8);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.pushState({ page: 'rent-asics' }, '', '/mining-store/rent-asics');
      window.location.reload();
    }
  };

  return (
    <div className="holder contract-review-container">
      <div>
        <div className="contract-review-container__title-block">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" className="contract-review-container__icon">
            <rect x="0.43125" y="1.025" width="22.95" height="22.95" rx="4.275" stroke="#1570EF" strokeWidth="1.05"></rect>
            <path d="M12.675 7.83333C12.675 8.56971 11.0129 9.16667 8.9625 9.16667C6.91214 9.16667 5.25 8.56971 5.25 7.83333M12.675 7.83333C12.675 7.09695 11.0129 6.5 8.9625 6.5C6.91214 6.5 5.25 7.09695 5.25 7.83333M12.675 7.83333V8.83333M5.25 7.83333V15.8333C5.25 16.5697 6.91214 17.1667 8.9625 17.1667M8.9625 11.8333C8.84873 11.8333 8.73616 11.8315 8.625 11.8279C6.73281 11.7667 5.25 11.1955 5.25 10.5M8.9625 14.5C6.91214 14.5 5.25 13.903 5.25 13.1667M18.75 12.1667C18.75 12.903 17.0879 13.5 15.0375 13.5C12.9871 13.5 11.325 12.903 11.325 12.1667M18.75 12.1667C18.75 11.4303 17.0879 10.8333 15.0375 10.8333C12.9871 10.8333 11.325 11.4303 11.325 12.1667M18.75 12.1667V17.1667C18.75 17.903 17.0879 18.5 15.0375 18.5C12.9871 18.5 11.325 17.903 11.325 17.1667V12.1667M18.75 14.6667C18.75 15.403 17.0879 16 15.0375 16C12.9871 16 11.325 15.403 11.325 14.6667" stroke="#1570EF" strokeWidth="1.05" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
          <p className="contract-review-container__title">Rent ASICs /</p>
          <p className="contract-review-container__subtitle">Payment Details</p>
        </div>
        <button className="ui-back contract-review-container__back" type="button" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15.8333 9.99996H4.16663M4.16663 9.99996L9.99996 15.8333M4.16663 9.99996L9.99996 4.16663" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
          <p className="ui-back__button">Back</p>
        </button>
        <div className="contract-review-container__grid">
          <div className="contract-review__efficiency">
            <h2 className="contract-review__section-title">Calculate contract efficiency</h2>
            <div className="contract-review__btc-price">
              <label className="contract-review__label">BTC target price</label>
              <div className="ui-input-slider contract-review__btc-price-slider">
                <div className="ui-input-slider__track">
                  <div style={{ width: '100%' }}></div>
                  <div className="ui-input-slider__progress" style={{ width: `${((btcPrice - 100000) / (250000 - 100000)) * 100}%` }}>
                    <div className="ui-input-slider__circle"></div>
                  </div>
                </div>
              </div>
              <div className="contract-review__slider-labels">
                <span>$100 000</span>
                <span>$250 000</span>
              </div>
              <div className="contract-review__btc-prices">
                <button 
                  className={`gray-bg contract-review__btc-price-btn ${btcPrice === 100000 ? 'active' : ''}`} 
                  type="button"
                  onClick={() => setBtcPrice(100000)}
                >
                  <div className="contract-review__btc-price-value">$100K</div>
                  <div className="contract-review__btc-price-label">Conservative</div>
                </button>
                <button 
                  className={`gray-bg contract-review__btc-price-btn ${btcPrice === 120000 ? 'active' : ''}`} 
                  type="button"
                  onClick={() => setBtcPrice(120000)}
                >
                  <div className="contract-review__btc-price-value">$120K</div>
                  <div className="contract-review__btc-price-label">Moderate</div>
                </button>
                <button 
                  className={`gray-bg contract-review__btc-price-btn ${btcPrice === 150000 ? 'active' : ''}`} 
                  type="button"
                  onClick={() => setBtcPrice(150000)}
                >
                  <div className="contract-review__btc-price-value">$150K</div>
                  <div className="contract-review__btc-price-label">Optimistic</div>
                </button>
                <button 
                  className={`gray-bg contract-review__btc-price-btn ${btcPrice === 200000 ? 'active' : ''}`} 
                  type="button"
                  onClick={() => setBtcPrice(200000)}
                >
                  <div className="contract-review__btc-price-value">$200K</div>
                  <div className="contract-review__btc-price-label">Positive</div>
                </button>
              </div>
            </div>
            <div className="contract-review__metric contract-review__metric--spacing">
              <div className="contract-review__metric-label">
                Expected mining output{' '}
                <div className="tooltip">
                  <div className="tooltip-text" style={{ display: 'none' }}>
                    This information is based on reference mining difficulty, FPPS and chosen USD/BTC rate. They are not guarantees of future results and should be viewed with caution due to market uncertainties. We assume no responsibility for decisions made or actions taken based on the content of this web page. Always consult with qualified professionals before making significant business decisions.
                  </div>
                  <svg className="default" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <g clipPath="url(#clip0_264_44934)">
                      <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                    </g>
                    <defs>
                      <clipPath id="clip0_264_44934">
                        <rect width="18" height="18" fill="white"></rect>
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              </div>
              <div className="contract-review__metric-value">
                <span className="contract-review__metric-btc">{formatBTC(calculations.totalBtcReward)} BTC</span>
                <span className="contract-review__metric-usd">= {formatCurrency(calculations.totalUsdReward)}</span>
              </div>
            </div>
            <div className="contract-review__metric contract-review__metric--green">
              <div className="contract-review__metric-label">Calculated return</div>
              <div className="contract-review__metric-value contract-review__metric-value--success">
                {formatCurrency(calculations.finalOutput)}{' '}
                <span className="contract-review__metric-usd">= ROI {calculations.returnPercent}%</span>
              </div>
            </div>
            <div className="contract-review__opportunity-cost">
              <div className="contract-review__opportunity-title">Every day without mining, you lose</div>
              <div className="contract-review__opportunity-value">{formatCurrency(Math.max(0, calculations.dailyLoss))}</div>
              <div className="contract-review__opportunity-desc">Start today and don't miss out on profits</div>
            </div>
          </div>
          <div className="contract-review__parameters">
            <div className="contract-review__basic-conditions">
              <h2 className="contract-review__section-title">Contract parameters</h2>
              <div className="contract-review__param-row">
                <span className="contract-review__param-label">TH Power</span>
                <span className="contract-review__param-value">{hashrate.toFixed(2)} TH/s</span>
              </div>
              <div className="contract-review__param-row">
                <span className="contract-review__param-label">Contract period</span>
                <span className="contract-review__param-value">{contractPeriod} months</span>
              </div>
              <div className="contract-review__param-row">
                <span className="contract-review__param-label">Electricity / Service fee (prepaid period)</span>
                <span className="contract-review__param-value">{prepaidMonths} months</span>
              </div>
              <div className="contract-review__param-row">
                <span className="contract-review__param-label">Prepaid for electricity</span>
                <span className="contract-review__param-value">{calculations.prepaidDiscountPercent.toFixed(0)}%</span>
              </div>
            </div>
            <div className="contract-review__contract-includes">
              <h2 className="contract-review__section-title contract-review__section-title--with-icon">What is included in your contract</h2>
              <div className="contract-review__calculation-section">
                <div className="contract-review__calculation-item">
                  <span className="contract-review__calculation-label">TH Power rental</span>
                  <span className="contract-review__calculation-value">{formatCurrency(calculations.baseContractPrice)}</span>
                </div>
                <div className="contract-review__calculation-formula">
                  {hashrate.toFixed(2)} TH/s × $0.008/day × {Math.round(calculations.daysInPeriod)} days
                </div>
                <div className="contract-review__calculation-item contract-review__calculation-item--discount">
                  <span className="contract-review__calculation-label">Period discount {contractPeriod} months ({calculations.periodDiscountPercent}%) </span>
                  <span className="contract-review__calculation-value contract-review__calculation-value--negative"> -{formatCurrency(calculations.periodDiscount)}</span>
                </div>
                <div className="contract-review__calculation-item contract-review__calculation-item--total">
                  <span className="contract-review__calculation-label">Contract (total)</span>
                  <span className="contract-review__calculation-value">{formatCurrency(calculations.contractPrice)}</span>
                </div>
              </div>
              <div className="contract-review__calculation-section">
                <div className="contract-review__calculation-item">
                  <span className="contract-review__calculation-label">Electricity / Service fee (prepaid)</span>
                  <span className="contract-review__calculation-value">{formatCurrency(calculations.prepaidElectricity)}</span>
                </div>
                <div className="contract-review__calculation-formula">
                  {hashrate.toFixed(2)} TH/s × $0.031/day × {Math.round((prepaidMonths / contractPeriod) * calculations.daysInPeriod)} days
                </div>
                <div className="contract-review__calculation-item contract-review__calculation-item--discount">
                  <span className="contract-review__calculation-label">Period discount {prepaidMonths} months ({calculations.prepaidDiscountPercent.toFixed(0)}%) </span>
                  <span className="contract-review__calculation-value contract-review__calculation-value--negative"> -{formatCurrency(calculations.prepaidDiscount)}</span>
                </div>
                <div className="contract-review__calculation-item contract-review__calculation-item--total">
                  <span className="contract-review__calculation-label">Electricity total</span>
                  <span className="contract-review__calculation-value">{formatCurrency(calculations.totalElectricityBase)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="contract-review__details">
          <div className="contract-review__payment-now">
            <div className="contract-review__payment-label">Payment now</div>
            <div className="contract-review__payment-value">{formatCurrency(paymentAmount)}</div>
          </div>
          <div className="contract-review__info">
            <span className="contract-review__info-text">
              The remaining {contractPeriod - prepaidMonths} months ({formatCurrency(calculations.remainingElectricity)}) of electricity are paid for from your mining rewards automatically on daily basis.
            </span>
          </div>
        </div>
        <div className="contract-review-container__actions">
          <button 
            className="primary contract-review-container__continue-btn" 
            type="button"
            onClick={() => {
              const url = `/order/payment/${hashrate}/${contractPeriod}/${prepaidMonths}/${Math.round(paymentAmount * 100) / 100}`;
              window.history.pushState({ page: 'payment' }, '', url);
              window.location.reload();
            }}
          >
            Continue to order
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractReview;

